import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  Descriptions,
  Spin,
  Button,
  Table,
  InputNumber,
  message,
  Typography,
  Space,
  type FormInstance,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  cartonApi,
  supplierApi,
  customerApi,
  productApi,
  productKey,
  parseProductKey,
  type CartonDTO,
  type CartonProductItemDTO,
  type ProductDTO,
  type UpdateCartonRequest,
} from '../api/kfc';
import ResponsiveDrawer from './ResponsiveDrawer';

const STATE_LABELS: Record<string, string> = {
  preparing: '待收货',
  inKFYWarehouse: '抵达凯飞越仓库',
  inAgentWarehouse: '抵达代理仓库(出发地)',
  readyForShipping: '准备运输',
  onTheWay: '在途运输',
  arrivedDstPort: '抵达目的港',
  recevied: '已签收',
};

const formatWeight = (v: number | undefined | null) => (v != null ? `${v} kg` : '-');

const computeVolumeCm3 = (length?: number | null, width?: number | null, height?: number | null) => {
  if (length == null || width == null || height == null) return null;
  return length * width * height;
};

const applyCartonToForm = (form: FormInstance, c: CartonDTO) => {
  form.setFieldsValue({
    cartonId: c?.cartonId,
    fromSupplierId: c?.fromSupplierId,
    belongClientId: c?.belongClientId,
    shipmentId: c?.shipmentId ?? '',
    measuredWeightKg: c?.measuredWeightKg,
    lengthCm: c?.lengthCm,
    widthCm: c?.widthCm,
    heightCm: c?.heightCm,
    billingWeightKg: c?.billingWeightKg,
  });
};

/** 状态历程 */
const StatesTimeline: React.FC<{
  states: Record<string, Record<string, unknown>>;
  stateLabels: Record<string, string>;
}> = ({ states, stateLabels }) => {
  const keys = Object.keys(states).sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    if (Number.isNaN(na)) return 1;
    if (Number.isNaN(nb)) return -1;
    return String(a).localeCompare(String(b));
  });
  return (
    <div style={{ marginTop: 8 }}>
      {keys.map((key) => {
        const node = states[key] as Record<string, unknown> | undefined;
        if (!node) return null;
        const state = (node.state as string) || '';
        const ts = node.timestamp as number | undefined;
        const operationLog = node.operationLog as Record<string, unknown> | undefined;
        const label = stateLabels[state] || state;
        return (
          <div key={key} style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>{key}. {label}</div>
            {ts != null && <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>时间：{new Date(ts).toLocaleString()}</div>}
            {state === 'inKFYWarehouse' && operationLog && typeof operationLog === 'object' && (
              <div style={{ fontSize: 12, color: '#333', marginTop: 8 }}>
                <>
                  {operationLog.arrivedBy !== undefined && <div>到达方式：{Number(operationLog.arrivedBy) === 0 ? '货拉拉' : Number(operationLog.arrivedBy) === 1 ? '快递' : String(operationLog.arrivedBy)}</div>}
                  {operationLog.queryParamOfShippment != null && String(operationLog.queryParamOfShippment) && <div>查询单号：{String(operationLog.queryParamOfShippment)}</div>}
                  {operationLog.pictures && typeof operationLog.pictures === 'object' && Object.keys(operationLog.pictures as Record<string, string>).length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ marginBottom: 4 }}>照片：</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {Object.entries(operationLog.pictures as Record<string, string>).map(([desc, base64]) => (
                          <div key={desc} style={{ textAlign: 'center' }}>
                            <img src={base64} alt={desc} style={{ maxWidth: 120, maxHeight: 90, objectFit: 'contain', border: '1px solid #eee', borderRadius: 4 }} />
                            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{desc || '照片'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ProductListInDrawer: React.FC<{
  products: CartonProductItemDTO[];
  readOnly?: boolean;
  onRemove: (index: number) => void;
  onAmountChange: (index: number, amount: number) => void;
  fetchProductByKey: (nameEN: string | undefined, hscode: string) => Promise<ProductDTO | null>;
}> = ({ products, readOnly = false, onRemove, onAmountChange, fetchProductByKey }) => {
  const [details, setDetails] = useState<Record<string, ProductDTO | null>>({});
  useEffect(() => {
    products.forEach((p) => {
      const hscode = p.hscode;
      if (!hscode) return;
      const cacheKey = productKey(p.nameEN, hscode);
      fetchProductByKey(p.nameEN, hscode).then((d) => setDetails((prev) => ({ ...prev, [cacheKey]: d })));
    });
  }, [products, fetchProductByKey]);

  const columns = [
    { title: '海关编码', dataIndex: 'hscode', key: 'hscode', width: 110, ellipsis: true },
    { title: '英文名称', dataIndex: 'nameEN', key: 'nameEN', width: 120, ellipsis: true },
    {
      title: '产品信息',
      key: 'detail',
      ellipsis: true,
      render: (_: unknown, record: CartonProductItemDTO) => {
        const cacheKey = productKey(record.nameEN, record.hscode);
        const d = record.hscode ? details[cacheKey] : undefined;
        if (d === undefined) return <span style={{ color: '#999' }}>加载中…</span>;
        if (d === null) return <span style={{ color: '#999' }}>未找到对应的产品档案</span>;
        const name = d.nameZN || d.nameEN || '-';
        const brand = d.brand ? `品牌：${d.brand}` : '';
        const price = d.price ? `USD ${d.price.USD ?? '-'} / RMB ${d.price.RMB ?? '-'}` : '';
        return (
          <div style={{ fontSize: 12 }}>
            <div>名称：{name}</div>
            {brand && <div>{brand}</div>}
            {price && <div>价格：{price}</div>}
          </div>
        );
      },
    },
    {
      title: '数量',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (val: number | undefined, _: CartonProductItemDTO, index: number) => (
        readOnly ? (
          <span>{val ?? 0}</span>
        ) : (
          <InputNumber min={1} value={val ?? 0} onChange={(v) => onAmountChange(index, v ?? 1)} size="small" style={{ width: 72 }} />
        )
      ),
    },
    ...(readOnly
      ? []
      : [{
          title: '操作',
          key: 'action',
          width: 80,
          render: (_: unknown, __: CartonProductItemDTO, index: number) => (
            <Button type="link" danger size="small" onClick={() => onRemove(index)}>删除</Button>
          ),
        }]),
  ];

  const dataSource = products.map((p, index) => ({ ...p, key: `${productKey(p.nameEN, p.hscode)}-${index}` }));
  if (!products.length) {
    return (
      <div style={{ color: '#999', marginTop: 8 }}>
        {readOnly ? '暂无产品' : '暂无产品，请从上方选择并添加'}
      </div>
    );
  }
  return <Table size="small" dataSource={dataSource} columns={columns} pagination={false} scroll={{ x: 400 }} />;
};

export interface CartonDetailDrawerProps {
  open: boolean;
  cartonId: string | undefined;
  onClose: () => void;
  onSaved?: () => void;
  /** 嵌入父级侧滑：不渲染 Drawer 外壳，由父级提供容器 */
  embedded?: boolean;
  /** 嵌入模式下「返回」回调 */
  onBack?: () => void;
}

/** 货物详情滑窗（统一样式、带关闭按钮），可复用于运单详情中的货物详情 */
const CartonDetailDrawer: React.FC<CartonDetailDrawerProps> = ({
  open,
  cartonId,
  onClose,
  onSaved,
  embedded = false,
  onBack,
}) => {
  const [detailCarton, setDetailCarton] = useState<CartonDTO | null>(null);
  /** 已保存基线，用于取消编辑时还原 */
  const [baselineCarton, setBaselineCarton] = useState<CartonDTO | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [detailForm] = Form.useForm();
  const [suppliers, setSuppliers] = useState<{ value: string; label: string }[]>([]);
  const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
  const [productOptions, setProductOptions] = useState<ProductDTO[]>([]);
  const [productDetailCache, setProductDetailCache] = useState<Record<string, ProductDTO | null>>({});
  const [selectedProductToAdd, setSelectedProductToAdd] = useState<string | null>(null);
  const lengthCm = Form.useWatch('lengthCm', detailForm);
  const widthCm = Form.useWatch('widthCm', detailForm);
  const heightCm = Form.useWatch('heightCm', detailForm);
  const volumeCm3 = computeVolumeCm3(lengthCm, widthCm, heightCm);

  const canEdit = detailCarton?.state !== 'onTheWay';

  const fetchProductByKey = useCallback((nameEN: string | undefined, hscode: string): Promise<ProductDTO | null> => {
    if (!hscode?.trim()) return Promise.resolve(null);
    const cacheKey = productKey(nameEN, hscode);
    if (productDetailCache[cacheKey] !== undefined) return Promise.resolve(productDetailCache[cacheKey]);
    const request = nameEN?.trim()
      ? productApi.getByKey(nameEN.trim(), hscode.trim())
      : productApi.getByHscode(hscode.trim());
    return request.then((r) => r.data).catch(() => null).then((p) => {
      setProductDetailCache((prev) => ({ ...prev, [cacheKey]: p }));
      return p;
    });
  }, [productDetailCache]);

  useEffect(() => {
    supplierApi.list().then((res) => {
      setSuppliers((res.data || []).map((s) => ({ value: s.id!, label: `${s.supplierName || s.supplierId || s.id}` })));
    }).catch(() => setSuppliers([]));
    customerApi.list().then((res) => {
      setCustomers((res.data || []).map((c) => ({ value: c.id!, label: `${c.name || c.customerCode || c.id}` })));
    }).catch(() => setCustomers([]));
    productApi.list({ view: 'summary' }).then((res) => setProductOptions(res.data || [])).catch(() => setProductOptions([]));
  }, []);

  useEffect(() => {
    if (!open || !cartonId) {
      setDetailCarton(null);
      setBaselineCarton(null);
      setEditing(false);
      setSelectedProductToAdd(null);
      return;
    }
    setDetailLoading(true);
    setDetailCarton(null);
    setBaselineCarton(null);
    setEditing(false);
    setSelectedProductToAdd(null);
    cartonApi
      .get(cartonId)
      .then((res) => {
        const c = res.data;
        setDetailCarton(c);
        setBaselineCarton(c);
        applyCartonToForm(detailForm, c);
      })
      .catch(() => message.warning('加载货物详情失败'))
      .finally(() => setDetailLoading(false));
  }, [open, cartonId, detailForm]);

  const startEditing = () => {
    if (!canEdit || !detailCarton) return;
    setEditing(true);
  };

  const cancelEditing = () => {
    if (!baselineCarton) {
      setEditing(false);
      return;
    }
    const restored: CartonDTO = {
      ...baselineCarton,
      products: (baselineCarton.products || []).map((p) => ({ ...p })),
    };
    setDetailCarton(restored);
    applyCartonToForm(detailForm, restored);
    setSelectedProductToAdd(null);
    setEditing(false);
  };

  const saveDetail = () => {
    if (!detailCarton?.id || !editing || !canEdit) return;
    detailForm.validateFields().then((values) => {
      const measured = values.measuredWeightKg as number | undefined;
      const billing = values.billingWeightKg as number | undefined;
      if (measured != null && billing != null && billing < measured) {
        message.error('计费重量不能小于测量重量');
        return;
      }
      setDetailSaving(true);
      const products: CartonProductItemDTO[] = (detailCarton.products || []).map((p) => ({
        hscode: p.hscode,
        nameEN: p.nameEN,
        amount: p.amount ?? 0,
      }));
      const body: UpdateCartonRequest = {
        cartonId: values.cartonId,
        fromSupplierId: values.fromSupplierId || undefined,
        belongClientId: values.belongClientId || undefined,
        products: products.length ? products : undefined,
        measuredWeightKg: values.measuredWeightKg ?? null,
        lengthCm: values.lengthCm ?? null,
        widthCm: values.widthCm ?? null,
        heightCm: values.heightCm ?? null,
        billingWeightKg: values.billingWeightKg ?? null,
      };
      cartonApi
        .update(detailCarton.id!, body)
        .then(() => {
          message.success('保存成功');
          const next: CartonDTO = {
            ...detailCarton,
            ...values,
            shipmentId: detailCarton.shipmentId,
            measuredWeightKg: values.measuredWeightKg ?? undefined,
            lengthCm: values.lengthCm ?? undefined,
            widthCm: values.widthCm ?? undefined,
            heightCm: values.heightCm ?? undefined,
            billingWeightKg: values.billingWeightKg ?? undefined,
            products: products.map((p) => ({ ...p })),
          };
          setDetailCarton(next);
          setBaselineCarton(next);
          setEditing(false);
          onSaved?.();
        })
        .catch((e) => message.error(e.response?.data?.message || '保存失败'))
        .finally(() => setDetailSaving(false));
    });
  };

  const addProductToDetail = (product: ProductDTO, amount: number = 1) => {
    if (!editing) return;
    const hscode = product.hscode;
    const nameEN = product.nameEN;
    if (!hscode || !nameEN) return;
    const key = productKey(nameEN, hscode);
    const exists = (detailCarton?.products || []).some((p) => productKey(p.nameEN, p.hscode) === key);
    if (exists) {
      message.info('该产品已在列表中');
      return;
    }
    setSelectedProductToAdd(null);
    setDetailCarton((prev) => (prev ? { ...prev, products: [...(prev.products || []), { hscode, nameEN, amount }] } : prev));
  };

  const removeProductFromDetail = (index: number) => {
    if (!editing) return;
    setDetailCarton((prev) => {
      if (!prev?.products?.length) return prev;
      const next = [...prev.products];
      next.splice(index, 1);
      return { ...prev, products: next };
    });
  };

  const setDetailProductAmount = (index: number, amount: number) => {
    if (!editing) return;
    setDetailCarton((prev) => {
      if (!prev?.products?.[index]) return prev;
      const next = [...prev.products];
      next[index] = { ...next[index], amount };
      return { ...prev, products: next };
    });
  };

  const renderState = (state: string) => STATE_LABELS[state] || state;

  const body = detailLoading ? (
    <Spin spinning />
  ) : detailCarton ? (
    <>
      <Form form={detailForm} layout="vertical" style={{ marginBottom: 24 }}>
        <Form.Item name="cartonId" label="货物标识" rules={[{ required: true }]}>
          <Input placeholder="货物标识" disabled={!editing} />
        </Form.Item>
        <Form.Item name="fromSupplierId" label="供应商">
          <Select placeholder="选择供应商" allowClear showSearch optionFilterProp="label" options={suppliers} style={{ width: '100%' }} disabled={!editing} />
        </Form.Item>
        <Form.Item name="belongClientId" label="客户">
          <Select placeholder="选择客户" allowClear showSearch optionFilterProp="label" options={customers} style={{ width: '100%' }} disabled={!editing} />
        </Form.Item>
        <Form.Item name="shipmentId" label="运单号">
          <Input placeholder="由运单管理绑定，不可在此修改" disabled />
        </Form.Item>
        <h4 style={{ marginBottom: 8 }}>重量与体积</h4>
        <Form.Item name="measuredWeightKg" label="测量重量（kg）">
          <InputNumber min={0} step={0.001} style={{ width: '100%' }} placeholder="测量重量" disabled={!editing} />
        </Form.Item>
        <Space style={{ display: 'flex', width: '100%' }} align="start">
          <Form.Item name="lengthCm" label="长（cm）" style={{ flex: 1 }}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="长" disabled={!editing} />
          </Form.Item>
          <Form.Item name="widthCm" label="宽（cm）" style={{ flex: 1 }}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="宽" disabled={!editing} />
          </Form.Item>
          <Form.Item name="heightCm" label="高（cm）" style={{ flex: 1 }}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="高" disabled={!editing} />
          </Form.Item>
        </Space>
        <Form.Item label="体积（cm³）">
          <Typography.Text type="secondary">
            {volumeCm3 != null ? volumeCm3 : '-'}
          </Typography.Text>
        </Form.Item>
        <Form.Item name="billingWeightKg" label="计费重量（kg）">
          <InputNumber min={0} step={0.001} style={{ width: '100%' }} placeholder="计费重量" disabled={!editing} />
        </Form.Item>
        {editing && (
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            计费重量需 ≥ 测量重量；按渠道泡重规则计算功能暂未开放，请手工填写。
          </Typography.Text>
        )}
      </Form>
      <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="状态">
          {renderState(detailCarton.state || '')}
          {!canEdit && (
            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
              （运输中不可编辑）
            </Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="计费重量">{formatWeight(detailCarton.billingWeightKg)}</Descriptions.Item>
        {detailCarton.batchId && (
          <Descriptions.Item label="批次标识">{detailCarton.batchId}</Descriptions.Item>
        )}
        {detailCarton.batchRemark && (
          <Descriptions.Item label="批次备注">{detailCarton.batchRemark}</Descriptions.Item>
        )}
      </Descriptions>
      <h4 style={{ marginBottom: 8 }}>产品列表</h4>
      {editing && (
        <div style={{ marginBottom: 8 }}>
          <Select
            placeholder="从已有产品中选择并添加"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            value={selectedProductToAdd}
            onChange={(key) => {
              if (!key) return;
              const { nameEN, hscode } = parseProductKey(key);
              const p = productOptions.find((o) => o.nameEN === nameEN && o.hscode === hscode);
              if (p) addProductToDetail(p);
            }}
            options={productOptions
              .filter((p) => p.hscode && p.nameEN)
              .map((p) => ({
                value: productKey(p.nameEN, p.hscode),
                label: `${p.hscode} ${p.nameZN || p.nameEN || ''}`.trim(),
              }))}
          />
        </div>
      )}
      <ProductListInDrawer
        products={detailCarton.products || []}
        readOnly={!editing}
        onRemove={removeProductFromDetail}
        onAmountChange={setDetailProductAmount}
        fetchProductByKey={fetchProductByKey}
      />
      <h4 style={{ marginTop: 24, marginBottom: 8 }}>状态历程</h4>
      {detailCarton.states && Object.keys(detailCarton.states).length > 0 ? (
        <StatesTimeline states={detailCarton.states} stateLabels={STATE_LABELS} />
      ) : (
        <div style={{ color: '#999', fontSize: 12 }}>暂无状态节点</div>
      )}
    </>
  ) : null;

  const actionButtons = (
    <Space>
      {editing ? (
        <>
          <Button onClick={cancelEditing} disabled={detailSaving}>取消</Button>
          <Button type="primary" loading={detailSaving} onClick={saveDetail}>保存</Button>
        </>
      ) : canEdit && detailCarton ? (
        <Button type="primary" onClick={startEditing}>编辑</Button>
      ) : !canEdit && detailCarton ? (
        <Typography.Text type="secondary">运输中不可编辑</Typography.Text>
      ) : null}
    </Space>
  );

  if (embedded) {
    if (!open) return null;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={onBack || onClose} style={{ paddingLeft: 0 }}>
            返回运单
          </Button>
          {actionButtons}
        </div>
        {body}
      </div>
    );
  }

  return (
    <ResponsiveDrawer
      title="货物详情"
      size="xl"
      open={open}
      onClose={onClose}
      closable
      extra={actionButtons}
    >
      {body}
    </ResponsiveDrawer>
  );
};

export default CartonDetailDrawer;
